import { Elements } from '@stripe/react-stripe-js'
import {
	AppButton,
	AppIconButton,
	Divider,
	Dropdown,
	Input,
	ListItem,
	MenuList,
	Modal,
	ModalHeader,
	PageHeader,
	StatCard
} from '@clickguard/design-system'
import { Calendar } from 'components/icons'
import { useThemeStore } from 'layout/navbar/store'
import moment from 'moment/moment'
import {
	Dispatch,
	Fragment,
	ReactNode,
	useEffect,
	useMemo,
	useState
} from 'react'
import { RiMoneyDollarCircleLine, RiMore2Line } from 'react-icons/ri'
import { TbShieldDollar } from 'react-icons/tb'
import { useAccountStore, useBillingStore, useUiStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { OfferDTO, OfferRequestDTO } from '../../../../../api-models'
import { GDropdown } from '../../../components/basic-blocks'
import { NewPaymentMethod } from '../../../components/new-payment-method'
import { RequestOffer } from '../../../components/request-offer'
import { PlanNames } from '../../../config'
import { useStores } from '../../../hooks'
import { useSubscriptionManagement } from '../../../hooks/usePendingSubscription'
import { Offers } from '../../../services/user'
import {
	formatCurrency,
	formatCurrencyDiscount,
	formatPriceInterval,
	formatSpend
} from '../../../utils'
import { PageTitle } from '../../../utils/page-title'

const cardTitle = (text: string) => (
	<span className="text-[18px] leading-6 font-medium text-t-base-blue">
		{text}
	</span>
)

export const OffersSetting = () => {
	const { account } = useAccountStore(
		useShallow((s) => ({
			account: s.account
		}))
	)
	const { offers, offersRequests, stripe } = useBillingStore(
		useShallow((s) => ({
			offers: s.offers,
			offersRequests: s.offersRequests,
			stripe: s.stripe
		}))
	)

	const [modalRequestOffer, setModalRequestOffer] = useState(false)

	const { theme } = useThemeStore()

	return (
		<div className="w-full">
			<PageTitle pageName="Offers" />
			<PageHeader
				layout="stacked"
				title="Offers"
				subtitle="Request and review custom quotes for your workspaces"
			/>
			<div className="flex flex-col gap-4 pt-4">
				{offers?.length > 0 && (
					<StatCard
						size="regular"
						divider={false}
						className="w-full !p-4 !gap-4"
						title={cardTitle('Prepared offers')}
					>
						<p className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
							Our staff has prepared the following offers
						</p>
						{offers?.map((offer) => (
							<Elements
								key={offer.id}
								stripe={stripe}
								options={{
									appearance: { theme: theme === 'dark' ? 'night' : 'stripe' }
								}}
							>
								<OfferList offer={offer} />
							</Elements>
						))}
					</StatCard>
				)}
				<RequestOffer
					open={modalRequestOffer}
					account={account}
					onClose={() => setModalRequestOffer(false)}
				/>
				<StatCard
					size="regular"
					divider={false}
					className="w-full !p-4 !gap-4"
					title={cardTitle('Requests')}
				>
					<div className="flex flex-col gap-5 rounded-xl border border-t-checkbox p-4">
						<div className="flex items-center justify-between gap-4">
							<p className="text-[16px] leading-[22px] text-[color:var(--text-secondary)]">
								If you need a custom plan, our staff can prepare you a quote
							</p>
							<AppButton
								variant="secondary"
								size="md"
								label="Request a quote"
								className="shrink-0 !border-t-card-border !text-t-base-blue"
								onClick={() => setModalRequestOffer(true)}
							/>
						</div>
						{offersRequests?.map((request) => (
							<Fragment key={request.id}>
								<Divider className="w-full" />
								<OfferRequestList offerRequest={request} />
							</Fragment>
						))}
					</div>
				</StatCard>
			</div>
		</div>
	)
}

const OfferRequestList = ({
	offerRequest
}: {
	offerRequest: OfferRequestDTO
}) => {
	const [modalRequestOffer, setModalRequestOffer] = useState(false)
	const {
		accountStore: { accounts },
		billingStore
	} = useStores()
	const deleteOfferRequest = async () => {
		await billingStore.removeOfferRequest(offerRequest.id)
	}

	const account = accounts.find((a) => a.id === offerRequest.account_id)
	const intervalLabel = offerRequest.interval
		? offerRequest.interval.charAt(0).toUpperCase() +
		  offerRequest.interval.slice(1)
		: ''

	return (
		<div className="flex items-center justify-between gap-4">
			<RequestOffer
				open={modalRequestOffer}
				onClose={() => setModalRequestOffer(false)}
				offerRequest={offerRequest}
				disabledFor={offerRequest.account_id ? 'NEW' : 'EXISTING'}
			/>
			<div className="flex flex-col gap-3">
				<p className="text-[18px] leading-6 font-medium text-t-base-blue">
					{account ? account.name : 'A new workspace'}
				</p>
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-1.5">
						<RiMoneyDollarCircleLine className="h-4 w-4 shrink-0 text-[color:var(--text-primary)]" />
						<span className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
							{formatSpend(offerRequest.spend)} Monthly ad spend
						</span>
					</div>
					<div className="flex items-center gap-1.5">
						<Calendar className="h-4 w-4 shrink-0 text-[color:var(--text-primary)]" />
						<span className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
							{intervalLabel} billing
						</span>
					</div>
				</div>
			</div>
			<div className="flex items-center gap-4">
				<GDropdown
					anchor="left"
					menuButton={
						<AppIconButton
							icon={<RiMore2Line className="h-4 w-4" />}
							aria-label="Offer actions"
							variant="secondary"
							size="md"
						/>
					}
				>
					{(close) => (
						<MenuList className="w-[180px]">
							<ListItem
								label="Remove"
								onClick={() => {
									deleteOfferRequest()
									close?.()
								}}
							/>
							<ListItem
								label="Update"
								onClick={() => {
									setModalRequestOffer(true)
									close?.()
								}}
							/>
						</MenuList>
					)}
				</GDropdown>
			</div>
		</div>
	)
}

const OfferList = (props: { offer: OfferDTO }) => {
	const { offer } = props
	const {
		accountStore: { accounts }
	} = useStores()
	const [close, setClose] = useState(true)

	const account = accounts.find((a) => a.id === offer.account_id)

	return (
		<div className="flex flex-col gap-5 rounded-xl border border-t-checkbox p-4">
			<div className="flex items-center justify-between gap-5">
				<div className="flex flex-col gap-3">
					<p className="text-[18px] leading-6 font-medium text-t-base-blue">
						{account ? account.name : 'A new workspace'}
					</p>
					<div className="flex items-center gap-4">
						<div className="flex items-center gap-1.5">
							<TbShieldDollar className="h-4 w-4 shrink-0 text-[color:var(--text-primary)]" />
							<span className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
								{formatSpend(offer.spend)} Monthly ad spend
							</span>
						</div>
					</div>
				</div>
				<AppButton
					variant={close ? 'primary' : 'secondary'}
					size="md"
					label={close ? 'See offer' : 'Close'}
					className="shrink-0"
					onClick={() => setClose(!close)}
				/>
			</div>
			{!close && (
				<ViewOfferDetails key={offer.id} offer={offer} setClose={setClose} />
			)}
		</div>
	)
}

const ViewOfferDetails = ({
	setClose,
	offer
}: {
	setClose: Dispatch<boolean>
	offer: OfferDTO
}) => {
	const { billingStore, accountStore } = useStores()
	const { isInSetup, getAllAccounts } = accountStore
	const isLoading = useUiStore((s) => s.isLoading)
	const [showNewPayment, setShowNewPayment] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [paymentMethodId, setPaymentMethodId] = useState<string>('')
	const [loading, setLoading] = useState(false)
	const [name, setName] = useState('')
	const [error, setErrors] = useState({ name: '' })

	const offerAccount = offer.account
	const subscriptionType = offerAccount?.billing?.subscription?.type
	const billingDate = moment(
		offerAccount?.billing?.subscription?.next_billing_date ||
			offerAccount?.billing?.subscription?.ends_at ||
			offerAccount?.billing?.subscription?.ended_at
	).format('ll')

	const fetchOffer = async () => {
		const offers = await Offers.find()
		billingStore.setOffers(offers)
	}

	const isDiscount = useMemo(() => {
		if (offer.coupon?.amount_off) {
			return offer.coupon.amount_off > 0
		}
		if (offer.coupon?.percent_off) {
			return offer.coupon.percent_off > 0
		}
		return false
	}, [offer?.coupon])

	// Strip the "ClickGUARD " prefix so plan values read Lite / Standard / Pro /
	// Custom Pro, per the Figma. Falls back to the raw tier if unmapped.
	const planLabel = (PlanNames[offer.tier] ?? offer.tier).replace(
		/^ClickGUARD\s+/i,
		''
	)
	const priceDisplay = `${formatCurrencyDiscount(offer.price, {
		amountOff: offer?.coupon?.amount_off,
		percentOff: offer?.coupon?.percent_off
	})}${formatPriceInterval(offer.interval, offer.interval_count)}`
	const billedLabel = offer.interval === 'year' ? 'Billed yearly' : 'Billed monthly'

	useSubscriptionManagement({
		subscription: accountStore.subscription,
		shouldLoad: loading,
		onSuccess: async () => {
			getAllAccounts()
			fetchOffer()
			setLoading(false)
			setClose(true)
		}
	})
	const reject = async () => {
		await billingStore.rejectOffer(offer.id)
		setClose(true)
	}

	const submitForNewAccount = async (isSchedule = false) => {
		setLoading(true)
		const subscription = await billingStore.acceptOffer(
			offer.id,
			isSchedule,
			paymentMethodId,
			offer?.account_id ? undefined : name,
			isInSetup()
		)
		if (subscription?.status === 'ACTIVE') {
			getAllAccounts()
			setLoading(false)
			setClose(true)
		}
	}

	const submitForCurrent = async (isSchedule = false) => {
		if (offer?.account_id) {
			await submitForNewAccount(isSchedule)
		} else {
			const error = handleErrors()
			if (error) {
				setErrors({ name: error })
				return
			}
			setShowNewPayment(true)
		}
	}

	useEffect(() => {
		if (paymentMethodId && !offer?.account_id) {
			submitForNewAccount()
		}
	}, [paymentMethodId])

	useEffect(() => {
		accountStore.setSubscription(null)
	}, [])

	const handleErrors = () => {
		if (!offer.account_id && name === '') {
			return 'You need to add a workspace name and select a payment method'
		} else {
			return ''
		}
	}

	// Discount is a dynamic column: rendered (header + cell) only when the sales
	// team applied a discount. Remaining columns redistribute via flex-1.
	const columns: { header: string; cell: ReactNode }[] = [
		{
			header: 'Plan',
			cell: (
				<span className="text-[14px] leading-[18px] text-[color:var(--text-primary)]">
					{planLabel}
				</span>
			)
		},
		{
			header: 'Monthly ad spend',
			cell: (
				<span className="text-[14px] leading-[18px] text-[color:var(--text-primary)]">
					{formatSpend(offer.spend)}
				</span>
			)
		},
		{
			header: 'Price',
			cell: (
				<span className="text-[14px] leading-[18px] text-[color:var(--text-primary)]">
					{priceDisplay}
				</span>
			)
		},
		...(isDiscount
			? [
					{
						header: 'Discount',
						cell: (
							<span className="inline-flex items-center justify-center whitespace-nowrap rounded-2xl border border-[var(--badge-green-border,#17b26a)] bg-[color:var(--badge-green-bg,#edfcf3)] px-1.5 py-0.5 text-[12px] font-semibold leading-4 text-[color:var(--badge-green-ink,#17b26a)]">
								{(offer?.coupon?.amount_off &&
									formatCurrency(offer?.coupon?.amount_off)) ||
									`${offer?.coupon?.percent_off}%`}{' '}
								{offer?.coupon?.duration
									? offer.coupon.duration.charAt(0).toUpperCase() +
									  offer.coupon.duration.slice(1)
									: ''}
							</span>
						)
					}
			  ]
			: [])
	]

	return (
		<div className="flex flex-col gap-3">
			{!offer?.account_id && (
				<>
					<NewPaymentMethod
						newWorkspace={true}
						show={showNewPayment}
						onClose={() => setShowNewPayment(false)}
						onSelect={(pm) => typeof pm === 'string' && setPaymentMethodId(pm)}
					/>
					<div className="text-left">
						<Input
							label="New workspace name"
							size="md"
							state={error.name ? 'error' : 'default'}
							error={error.name || undefined}
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
				</>
			)}

			<div className="w-full overflow-x-auto">
				<div className="min-w-[680px] overflow-hidden rounded-xl">
					<div className="flex bg-table-band">
						{columns.map((col) => (
							<div
								key={col.header}
								className="flex h-11 flex-1 items-center border-b border-t-checkbox px-6 py-3"
							>
								<span className="text-[12px] leading-4 font-semibold text-[color:var(--text-secondary)]">
									{col.header}
								</span>
							</div>
						))}
						<div className="h-11 flex-1 border-b border-t-checkbox" />
					</div>
					<div className="flex items-center">
						{columns.map((col) => (
							<div
								key={col.header}
								className="flex min-h-[72px] flex-1 items-center px-6 py-4"
							>
								{col.cell}
							</div>
						))}
						<div className="flex min-h-[72px] flex-1 items-center justify-end gap-3 px-6 py-4">
							<AppButton
								loader="dots"
								variant="secondary"
								size="md"
								label="Reject"
								disabled={isLoading('USER_REMOVE_OFFER')}
								isLoading={isLoading('USER_REMOVE_OFFER') || loading}
								onClick={reject}
							/>
							<AppButton
								loader="dots"
								variant="primary"
								size="md"
								label="Accept"
								isLoading={isLoading('USER_ACCEPT_OFFER') || loading}
								onClick={() =>
									offer?.account_id
										? setShowConfirmModal(true)
										: submitForCurrent(subscriptionType !== 'TRIAL')
								}
							/>
						</div>
					</div>
				</div>
			</div>

			{offerAccount && (
				<div className="flex items-center justify-center gap-1 text-[14px] leading-[18px]">
					<span className="text-[color:var(--text-secondary)]">
						*Your new subscription can be activated right now or your next
						billing date ({billingDate})
					</span>
				</div>
			)}

			{offer?.account_id && (
				<ConfirmPlanChangesModal
					open={showConfirmModal}
					onClose={() => setShowConfirmModal(false)}
					planLabel={planLabel}
					billedLabel={billedLabel}
					priceDisplay={priceDisplay}
					billingDate={billingDate}
					isLoading={isLoading('USER_ACCEPT_OFFER') || loading}
					onConfirm={(schedule) => submitForNewAccount(schedule)}
				/>
			)}
		</div>
	)
}

const ConfirmPlanChangesModal = ({
	open,
	onClose,
	planLabel,
	billedLabel,
	priceDisplay,
	billingDate,
	isLoading,
	onConfirm
}: {
	open: boolean
	onClose: () => void
	planLabel: string
	billedLabel: string
	priceDisplay: string
	billingDate: string
	isLoading: boolean
	onConfirm: (schedule: boolean) => void
}) => {
	const [timing, setTiming] = useState<'now' | 'next'>('now')

	return (
		<Modal
			open={open}
			onClose={onClose}
			className="!w-[520px] !max-w-[520px] !rounded-[12px] border border-t-checkbox p-6"
		>
			{/* gap-32 between the content block and the footer buttons (Figma) */}
			<div className="flex w-full flex-col gap-8">
				{/* gap-24 between header and summary (Figma) */}
				<div className="flex w-full flex-col gap-6">
					<ModalHeader
						size="sm"
						title="Confirm plan changes"
						onClose={onClose}
						// Figma: Text lg/Medium 18/24, text/brand-dark #171053 (DS default is 20/SemiBold navy).
						className="[&_h2]:!text-[18px] [&_h2]:!font-medium [&_h2]:!leading-6 [&_h2]:!tracking-normal [&_h2]:!text-t-base-blue"
					/>
					{/* gap-16 between summary rows (Figma) */}
					<div className="flex w-full flex-col gap-4">
						<div className="flex w-full items-center justify-between gap-6">
							<div className="flex flex-col gap-0.5">
								<span className="text-[16px] leading-[22px] text-[color:var(--text-primary)]">
									{planLabel} Plan
								</span>
								<span className="text-[14px] leading-[18px] text-[color:var(--text-tertiary,#9f9ea3)]">
									{billedLabel}
								</span>
							</div>
							<span className="text-[14px] font-medium leading-[18px] text-[color:var(--text-primary)]">
								{priceDisplay}
							</span>
						</div>
						<Divider className="w-full" />
						<div className="flex w-full items-center justify-between gap-4">
							<Dropdown
								value={timing}
								onValueChange={(value) => setTiming(value as 'now' | 'next')}
								options={[
									{ value: 'now', label: 'Update now' },
									{ value: 'next', label: 'Update next billing cycle' }
								]}
								size="md"
								menuWidth="trigger"
								// Figma: fixed 228px trigger, label left / chevron right.
								className="w-[228px] [&_button]:!w-full [&_button]:!justify-between"
							/>
							<span className="text-[14px] font-medium leading-[18px] text-[color:var(--text-primary)]">
								{timing === 'now'
									? 'New subscription will start today'
									: `Will start on ${billingDate}`}
							</span>
						</div>
					</div>
				</div>
				{/* footer: gap-10, right-aligned (Figma) */}
				<div className="flex w-full items-center justify-end gap-2.5">
					<AppButton
						variant="secondary"
						size="md"
						label="Cancel"
						disabled={isLoading}
						onClick={onClose}
					/>
					<AppButton
						loader="dots"
						variant="primary"
						size="md"
						label="Change plan"
						isLoading={isLoading}
						onClick={() => onConfirm(timing === 'next')}
					/>
				</div>
			</div>
		</Modal>
	)
}
